import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    Flex,
    Link,
    Stack,
    Text,
} from '@chakra-ui/react';
import TextInput from '../../../../shared/components/TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormErrorResetEffect from "../LoginPage/FormErrorResetEffect";
import { useAppToast } from "../../../../hooks/useAppToast";
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";

const ResetPasswordConfirmPage = () => {
    const { resetPasswordWithToken } = useAuth();
    const { t } = useTranslation('default');
    const toast = useAppToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [error, setError] = useState(null);

    const token = searchParams.get('token');
    const emailFromLink = searchParams.get('email') || '';

    useEffect(() => () => setError(null), []);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                email: Yup.string().email(t('form.email.message')).required(t('form.email.required')),
                password: Yup.string()
                    .min(8, t('form.password.length'))
                    .required(t('form.password.required')),
                confirmPassword: Yup.string()
                    .oneOf([Yup.ref('password'), null], t('form.password.match'))
                    .required(t('form.password.required')),
            }),
        [t]
    );

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setError(null);
        try {
            await resetPasswordWithToken({ token, email: values.email, password: values.password });
            resetForm();
            toast({
                title: t('reset.confirm.successTitle'),
                description: t('reset.confirm.successMessage'),
                status: 'success',
                duration: 6000,
                isClosable: true,
            });
            navigate('/login');
        } catch (e) {
            if (e?.response?.data?.code === 'SamePasswordNotAllowed') {
                setError(t('reset.confirm.samePasswordError'));
            } else {
                setError(e?.response?.data?.message || 'reset.confirm.error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <Flex bgColor="#F3F3F3" w="100%" minH="100vh" align="center" justify="center" px={{ base: 4, sm: 6 }}>
                <Box maxW="xl" p={6} bg="white" borderRadius="md" boxShadow="md">
                    <Text fontWeight="bold" mb={2}>{t('reset.confirm.invalidLinkTitle')}</Text>
                    <Text mb={6}>{t('reset.confirm.invalidLinkMessage')}</Text>
                    <Link href="/reset-password" color="#089A7A" _hover={{ textDecoration: 'underline' }}>
                        {t('reset.confirm.requestAgain')}
                    </Link>
                </Box>
            </Flex>
        );
    }

    return (
        <Flex bgColor="#F3F3F3" w="100%" minH="100vh" align="center" justify="center" px={{ base: 4, sm: 6 }}>
            <Box w="full" align="center" display="flex" gap={'48px'} flexDirection="column" alignItems="center">
                <Stack spacing={6} align="center" mb={8}>
                    <Text className="login-title">{t('reset.confirm.title')}</Text>
                    <Text className="login-message" lineHeight={{ base: '16px', sm: 'sm' }} fontSize={{ base: 20, sm: 24 }}>
                        {t('reset.confirm.message')}
                    </Text>
                </Stack>

                <Box maxW="3xl" w="full" bg="transparent" gap={'48px'}>
                    <Formik
                        initialValues={{ email: emailFromLink, password: '', confirmPassword: '' }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnChange
                        validateOnBlur
                        enableReinitialize
                    >
                        {({ isSubmitting, errors, touched, isValid, dirty }) => (
                            <Form>
                                <FormErrorResetEffect resetError={() => setError(null)} />
                                <Flex flexDirection="column" gap={'16px'}>
                                    <TextInput
                                        label={t('form.email.label')}
                                        name="email"
                                        type="email"
                                        placeholder={t('form.email.placeholder')}
                                        errors={errors}
                                        touched={touched}
                                    />

                                    <TextInput
                                        label={t('reset.confirm.newPasswordLabel')}
                                        name="password"
                                        type="password"
                                        placeholder={t('reset.confirm.newPasswordPlaceholder')}
                                        errors={errors}
                                        touched={touched}
                                    />

                                    <TextInput
                                        label={t('reset.confirm.repeatNewPasswordLabel')}
                                        name="confirmPassword"
                                        type="password"
                                        placeholder={t('reset.confirm.repeatNewPasswordPlaceholder')}
                                        errors={errors}
                                        touched={touched}
                                    />
                                </Flex>

                                {error && (
                                    <Text color="red.500" textAlign="center" mt={4}>
                                        {t(error)}
                                    </Text>
                                )}

                                <Box display="flex" flexDirection="column" alignItems="center" mt={8}>
                                    <Button
                                        className="login-button"
                                        type="submit"
                                        isLoading={isSubmitting}
                                        gap={'10px'}
                                        bg="#089A7A"
                                        _hover={{ bg: '#1A7743' }}
                                        h={'59px'}
                                        w={'250px'}
                                        borderRadius={'4px'}
                                        isDisabled={isSubmitting || !isValid || !dirty}
                                    >
                                        <Text className="login-button-label">{t('reset.confirm.button')}</Text>
                                    </Button>
                                </Box>

                                <Box textAlign="center" mt={6}>
                                    <Link href="/login" color="#089A7A" _hover={{ textDecoration: 'underline' }}>
                                        {t('reset.request.backToLogin')}
                                    </Link>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Box>
        </Flex>
    );
};

export default ResetPasswordConfirmPage;
